import React, { useState, useEffect } from 'react';
import {
    Container,
    Box,
    Typography,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tabs,
    Tab,
    Chip,
    CircularProgress,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import BadgeCard from '../components/BadgeCard';
import { getBadges } from '../apiBadge';
import { BADGE_TYPES } from '../data/badgeTypes';
import Footer from '../../core/Footer';

const AwardsPage = () => {
    const [badges, setBadges] = useState([]);
    const [filterType, setFilterType] = useState('all');
    const [activeYear, setActiveYear] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getBadges().then(data => {
            if (data && !data.error) {
                setBadges(data);
                const years = [...new Set(data.map(b => b.year))].sort((a, b) => b - a);
                if (years.length > 0) setActiveYear(years[0]);
            }
            setLoading(false);
        });
    }, []);

    const years = [...new Set(badges.map(b => b.year))].sort((a, b) => b - a);

    const filtered = badges.filter(b => {
        const typeMatch = filterType === 'all' || b.badgeTypeId === filterType;
        const yearMatch = !activeYear || b.year === activeYear;
        return typeMatch && yearMatch;
    });

    return (
        <>
            {/* Hero Section */}
            <Box sx={{ backgroundColor: '#003E7E', color: 'white', py: 6, mb: 4 }}>
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <EmojiEventsIcon sx={{ fontSize: 64 }} />
                        <Box>
                            <Typography variant="h2" component="h1" sx={{ fontWeight: 700 }}>
                                Digital Badges
                            </Typography>
                            <Typography variant="h5" sx={{ opacity: 0.9 }}>
                                Verified SDSIC award credentials
                            </Typography>
                        </Box>
                    </Box>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ mb: 6 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress sx={{ color: '#003E7E' }} />
                    </Box>
                ) : badges.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <EmojiEventsIcon sx={{ fontSize: 80, color: '#003E7E', opacity: 0.3, mb: 2 }} />
                        <Typography variant="h5" sx={{ color: '#003E7E', mb: 1 }}>
                            No Digital Badges Issued Yet
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Digital badges will appear here once they are issued.
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {/* Year Tabs */}
                        {years.length > 0 && (
                            <Box sx={{ mb: 4 }}>
                                <Tabs
                                    value={activeYear || false}
                                    onChange={(e, val) => setActiveYear(val)}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    sx={{
                                        mb: 3,
                                        '& .MuiTab-root': {
                                            fontWeight: 700,
                                            fontSize: '1.1rem',
                                            minWidth: 100,
                                        },
                                        '& .Mui-selected': { color: '#003E7E' },
                                        '& .MuiTabs-indicator': { backgroundColor: '#FF6600', height: 3 },
                                    }}
                                >
                                    {years.map(y => (
                                        <Tab key={y} label={y} value={y} />
                                    ))}
                                </Tabs>
                            </Box>
                        )}

                        {/* Filter by award type */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                            <FormControl size="small" sx={{ minWidth: 220 }}>
                                <InputLabel>Filter by Award</InputLabel>
                                <Select
                                    value={filterType}
                                    label="Filter by Award"
                                    onChange={(e) => setFilterType(e.target.value)}
                                >
                                    <MenuItem value="all">All Awards</MenuItem>
                                    {Object.values(BADGE_TYPES).map(bt => (
                                        <MenuItem key={bt.id} value={bt.id}>{bt.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Chip
                                label={`${filtered.length} badge${filtered.length !== 1 ? 's' : ''}`}
                                sx={{ backgroundColor: '#003E7E', color: 'white', fontWeight: 600 }}
                            />
                        </Box>

                        {/* Year heading */}
                        {activeYear && (
                            <Typography variant="h4" sx={{ fontWeight: 700, color: '#003E7E', mb: 1 }}>
                                {activeYear} Awards
                            </Typography>
                        )}
                        {activeYear && (
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                                Recognizing outstanding contributions to the SDS Industry Consortium
                            </Typography>
                        )}

                        {/* Badge cards grid */}
                        {filtered.length > 0 ? (
                            <Grid container spacing={3}>
                                {filtered.map((badge, i) => (
                                    <Grid item xs={12} sm={6} md={4} key={badge._id || i}>
                                        <BadgeCard badge={badge} />
                                    </Grid>
                                ))}
                            </Grid>
                        ) : (
                            <Box sx={{ textAlign: 'center', py: 6 }}>
                                <Typography variant="h6" color="text.secondary">
                                    No badges match the current filter.
                                </Typography>
                            </Box>
                        )}
                    </>
                )}
            </Container>

            <Footer />
        </>
    );
};

export default AwardsPage;
